// The Columns popover for DataTableToolbar: show/hide checkboxes plus
// drag-to-reorder, and the column-order math behind it. The toolbar only
// renders <ColumnReorderPopover>; the dnd-kit wiring stays in this file.
import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, Eye, GripVertical } from 'lucide-react';
import * as React from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    type AppColumnMeta,
    type Column,
    type RowData,
    type Table as TableType,
} from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';

export function getColumnDisplayName<TData extends RowData>(
    column: Column<TData, unknown>,
): string {
    const meta = column.columnDef.meta as AppColumnMeta | undefined;
    const label = column.columnDef.header as React.ReactNode;
    return (
        meta?.displayName ||
        (typeof label === 'string'
            ? label
            : (column.id.charAt(0).toUpperCase() + column.id.slice(1)).replace(
                  /_/g,
                  ' ',
              ))
    );
}

// TanStack appends any leaf column absent from columnOrder at the END, so the
// pinned ids (`meta.pinnedLeft`) must be placed explicitly at the front.
export function computeReorderedColumnIds(
    pinnedIds: ReadonlyArray<string>,
    reorderableIds: ReadonlyArray<string>,
    activeId: string,
    overId: string,
): Array<string> {
    const oldIndex = reorderableIds.indexOf(activeId);
    const newIndex = reorderableIds.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) {
        return [...pinnedIds, ...reorderableIds];
    }
    return [
        ...pinnedIds,
        ...arrayMove([...reorderableIds], oldIndex, newIndex),
    ];
}

function SortableColumnRow({
    id,
    label,
    checked,
    disabled,
    onCheckedChange,
}: {
    readonly id: string;
    readonly label: string;
    readonly checked: boolean;
    readonly disabled: boolean;
    readonly onCheckedChange: (checked: boolean) => void;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'flex items-center gap-2 rounded-sm px-1 py-1.5',
                isDragging && 'bg-muted',
            )}
        >
            <button
                type="button"
                aria-label={`Reorder ${label}`}
                className="text-muted-foreground hover:text-foreground flex h-6 w-5 shrink-0 cursor-grab items-center justify-center"
                {...attributes}
                {...listeners}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <Checkbox
                id={`column-toggle-${id}`}
                checked={checked}
                disabled={disabled}
                onCheckedChange={(value) => onCheckedChange(value === true)}
            />
            <label
                htmlFor={`column-toggle-${id}`}
                className="flex-1 cursor-pointer text-sm capitalize"
            >
                {label}
            </label>
        </div>
    );
}

export function ColumnReorderPopover<TData extends RowData>({
    table,
    buttonClassName,
}: {
    readonly table: TableType<TData>;
    readonly buttonClassName?: string;
}) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );
    const leafColumns = table.getAllLeafColumns();
    const leafById = new Map(leafColumns.map((column) => [column.id, column]));
    const listedIds = table.state.columnOrder.filter((id) => leafById.has(id));
    const listedSet = new Set(listedIds);
    const effectiveOrder = [
        ...listedIds,
        ...leafColumns
            .map((column) => column.id)
            .filter((id) => !listedSet.has(id)),
    ];
    const pinnedIds = leafColumns
        .filter(
            (column) =>
                (column.columnDef.meta as AppColumnMeta | undefined)
                    ?.pinnedLeft === true,
        )
        .map((column) => column.id);
    const pinnedSet = new Set(pinnedIds);
    const reorderableIds = effectiveOrder.filter((id) => !pinnedSet.has(id));

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        table.setColumnOrder(
            computeReorderedColumnIds(
                pinnedIds,
                reorderableIds,
                String(active.id),
                String(over.id),
            ),
        );
    };

    return (
        <Popover>
            <PopoverTrigger asChild={true}>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label="Toggle columns"
                    className={buttonClassName}
                >
                    <Eye className="h-4 w-4 md:mr-2" />
                    <span className="hidden md:inline">Columns</span>
                    <ChevronDown className="ml-1 hidden h-4 w-4 md:inline-block" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="flex max-h-[var(--radix-popover-content-available-height)] w-56 flex-col p-1"
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={reorderableIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                            {reorderableIds.map((id) => {
                                const column = leafById.get(id);
                                if (!column) {
                                    return null;
                                }
                                return (
                                    <SortableColumnRow
                                        key={id}
                                        id={id}
                                        label={getColumnDisplayName(column)}
                                        checked={column.getIsVisible()}
                                        disabled={!column.getCanHide()}
                                        onCheckedChange={(checked) =>
                                            column.toggleVisibility(checked)
                                        }
                                    />
                                );
                            })}
                        </div>
                    </SortableContext>
                </DndContext>
                <div className="mt-1 border-t pt-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => table.setColumnOrder([])}
                    >
                        Reset order
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
